import * as d3 from 'd3';

import { SvgManager } from '../../SvgManager';
import { chartXs, chartSm, ICategoricalChartOptions, RoundingHelper } from '../../..';
import { Title, PlotArea } from '../../pieces';
import { SingleValueChartData } from '../../../models/SingleValueChartData';
import { HorizontalCategoricalChartRenderer } from './HorizontalCategoricalChart.renderer';

export class HorizontalCategoricalChart {
    private _height: number;
    private _width: number;
    // private _mappedData: DoughnutChartData[];
    private _renderer: HorizontalCategoricalChartRenderer;
    private _options: ICategoricalChartOptions = {} as any;
    private _title: Title;
    // private _legend: Legend;
    private _margins = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    };
    private _plotMargins = {
        top: 10,
        bottom: 25,
        left: 60,
        right: 20
    };
    private _plotArea: d3.Selection<any>;
    private _chartArea: d3.Selection<any>;
    private _roudingHelper: RoundingHelper;
    private _data: SingleValueChartData[];
    private _pa: PlotArea;

    constructor(private _svgManager: SvgManager) {
        this._width = this._svgManager.getSize().width;
        this._height = this._svgManager.getSize().height;
        this._data = [];
        const container = this._svgManager.container();
        this._roudingHelper = new RoundingHelper();
        this.init(container);
        this._svgManager.addResizeFunction((width: number, height: number) => {
            this.resize(width, height);
        });
    }

    update(data: SingleValueChartData[]) {
        this._data = data;
        this.render(data);
    }

    options(): ICategoricalChartOptions;
    options(value: ICategoricalChartOptions): this;
    options(value?: ICategoricalChartOptions): this | ICategoricalChartOptions {
        if (arguments.length) {
            this._options = value;
            return this;
        }
        return this._options;
    }

    private render(data: any[]) {
        this._data = data;
        const min = Math.min(0, d3.min(data, d => d.value));
        const max = d3.max(data, d => d.value);
        const rounded = this._roudingHelper.round(min, max);
        this._title.translate((this._width) / 2, -10 - (this._options.isTitleVisible ? 0 : this._title.height()));
        this._title.isVisible(!!this._options.isTitleVisible);
        this._title.text(this._options.title);
        const leftPlotMargin = this.leftPlotMargin();
        this._plotArea
            .attr('transform', `translate(${leftPlotMargin},${this._plotMargins.top +
                (this._title.isVisible() ? this._title.height() : 0)})`);
        this._renderer
            .options({
                'bars': this._options.isRectangleVisible,
                'verticalLines': this._options.isVerticalLineVisible,
                'labels': this._options.isLabelsVisible
            })
            .width(this.plotWidth())
            .height(this.plotHeight())
            .xDomain([rounded.min, rounded.max])
            .xTickFormat(this._options.xTickFormat)
            .yWidth(leftPlotMargin)
            .render(data);
    }

    private resize(width: number, height: number) {
        this._width = width;
        this._height = height;
        const xs = width < chartXs || height < chartXs;
        const sm = !xs && (width < chartSm || height < chartSm);
        this._svgManager.container()
            .select('g.container')
            .classed('sm', sm)
            .classed('xs', xs);
        this._pa.resizeFunction()(this.plotWidth(), this.plotHeight());
        this.render(this._data);
    }

    private leftPlotMargin() {
        let leftMargin = this._plotMargins.left;
        if (this._options.minChartWidth && this._options.minChartWidth > 0 && this._data && this._data.length) {
            // Measure the text width
            const longestText = this._data.map(d => d.label)
                .sort((a, b) => a.length > b.length ? -1 : 1)[0];
            const txt = this._svgManager.container()
                .append('g')
                .classed('tickLabel', true)
                .append('text')
                .style('visibility', 'hidden')
                .text(longestText);
            const textWidth = (txt.node() as any).getBBox().width;
            txt.remove();

            leftMargin = textWidth + 9; //9 because of spacing form the axis - d3 value

            // relative min width
            // leftMargin = Math.min(leftMargin, this.chartWidth() * (1 - this._options.minChartWidth) - this._plotMargins.right);
            // Absolut min width
            leftMargin = Math.min(leftMargin, this.chartWidth() - this._options.minChartWidth - this._plotMargins.right);
        }
        return leftMargin;
    }

    private chartHeight(): number {
        const chartHeight = this._height - this._margins.top - this._margins.bottom;
        return chartHeight;
    }

    private chartWidth(): number {
        const chartWidth = this._width - this._margins.left - this._margins.right;
        return chartWidth;
    }

    private plotHeight(): number {
        const plotHeight = this.chartHeight() - this._plotMargins.top - this._plotMargins.bottom
            - (this._title.isVisible() ? this._title.height() : 0);
        return plotHeight;
    }

    private plotWidth(): number {
        const plotWidth = this.chartWidth() - this.leftPlotMargin() - this._plotMargins.right;
        return plotWidth;
    }

    private init(container: d3.Selection<any>) {
        this._chartArea = container
            .append('g')
            .classed('chart-area', true)
            .attr('transform', `translate(${this._margins.left},${this._margins.top})`);
        this._title = new Title(this._chartArea);
        const plotWidth = this.plotWidth();
        const plotHeight = this.plotHeight();
        const pa = new PlotArea(this._chartArea, plotWidth, plotHeight);
        this._pa = pa;
        this._plotArea = pa.group()
            .attr('transform', `translate(${this._plotMargins.left},${this._plotMargins.top +
                (this._title.isVisible() ? this._title.height() : 0)})`);
        this._renderer = new HorizontalCategoricalChartRenderer(this._plotArea.append('g'), plotWidth, plotHeight, pa.clipPathId());
        this._title.translate(this._width / 2, -10);
    }
}